import { EXCLUDED_ISSUER_ORG_CODE } from '../constants/userTagGroupLabels';
import { validateEmail } from './email';
import { validateFurigana, validatePostalCode, validateTel } from './validators';

// サーバ側 API のレスポンス errors キー名揺らぎを吸収するためのエイリアス。
// shopForm のキー (=shop API リクエスト名) と申請 API のキー名の対応:
//
// | shopForm キー                  | shop API (shops.lisp)        | 申請 API (shop-applications.lisp) |
// | ------------------------------ | ---------------------------- | ----------------------------------- |
// | shop_name                      | shop_name                    | shop_name                           |
// | shop_name_furigana             | shop_name_furigana           | shop_name_furigana                  |
// | shop_postal_code               | shop_postal_code             | postal_code             ★ alias    |
// | shop_address                   | shop_address                 | address                 ★ alias    |
// | shop_tel                       | shop_tel                     | tel                     ★ alias    |
// | shop_email                     | shop_email                   | email                   ★ alias    |
// | shop_external_id               | shop_external_id             | (申請 API 未対応)                  |
// | contact_person_name            | contact_person_name          | contact_person_name                 |
// | contact_person_name_furigana   | contact_person_name_furigana | contact_person_name_furigana        |
// | organization_code              | organization_code            | organization_code                   |
// | private_money_ids              | private_money_ids            | private_money_ids                   |
//
// → alias が必要なのは shop_* prefix 付きで申請 API 側で prefix が落ちる 4 キーのみ。
const PARAM_KEY_ALIAS = {
  shop_postal_code: ['shop_postal_code', 'postal_code'],
  shop_address: ['shop_address', 'address'],
  shop_tel: ['shop_tel', 'tel'],
  shop_email: ['shop_email', 'email'],
};

/**
 * errorResponse.errors[kind] (= missing / invalid / unpermitted の配列) に
 * 指定の shopForm key (またはそのエイリアス) が含まれるかを判定する純粋関数。
 */
export const hasParameterError = (errorResponse, kind, shopFormKey) => {
  if (!errorResponse || errorResponse.type !== 'invalid_parameters') return false;
  const list = errorResponse.errors && errorResponse.errors[kind];
  if (!Array.isArray(list)) return false;
  const aliases = PARAM_KEY_ALIAS[shopFormKey] || [shopFormKey];
  return aliases.some((key) => list.indexOf(key) !== -1);
};

/**
 * 選択中の加盟マネー一覧から発行体組織コードを 1 つ抽出する。
 * "pocketchange" 発行体は除外。
 * @param {Array} selectedMoneys - PrivateMoney オブジェクト配列
 * @returns {string | null}
 */
export const pickIssuerOrgCodeFromMoneys = (selectedMoneys) => {
  const money = pickIssuerPrivateMoneyFromMoneys(selectedMoneys);
  return money ? money.organization?.code ?? null : null;
};

/**
 * 選択中の加盟マネー一覧から発行体の PrivateMoney オブジェクトを 1 つ抽出する。
 * "pocketchange" 発行体は除外。
 * @param {Array} selectedMoneys - PrivateMoney オブジェクト配列
 * @returns {object | null}
 */
export const pickIssuerPrivateMoneyFromMoneys = (selectedMoneys) => {
  if (!Array.isArray(selectedMoneys)) return null;
  for (const money of selectedMoneys) {
    const code = money?.organization?.code;
    if (code && code !== EXCLUDED_ISSUER_ORG_CODE) return money;
  }
  return null;
};

// 加盟組織モード時に追加で必須となるフィールド (申請 API スキーマ準拠)
const MEMBER_ORG_REQUIRED_FIELDS = [
  'shop_name_furigana',
  'shop_postal_code',
  'shop_address',
  'shop_tel',
  'shop_email',
  'contact_person_name',
  'contact_person_name_furigana',
];

const isEmpty = (value) =>
  !value || (typeof value === 'string' && value.trim() === '');

/**
 * 店舗フォームの validation を一括実行する純粋関数。
 * CreateShop / UpdateShop の onClick から共通呼び出しされる。
 *
 * @param {object} shopForm    state.shopForm
 * @param {object} options
 * @param {boolean} options.isMemberOrgUser
 * @param {boolean} options.isMoneySelected
 * @returns {{
 *   hasError: boolean,
 *   showMoneySelectionError: boolean,
 *   showShopNameError: boolean,
 *   emailError: string | false,
 *   emptyFieldErrors: Record<string, true>,
 *   formatFieldErrors: Record<string, string>,
 * }}
 */
export const validateShopForm = (shopForm, { isMemberOrgUser, isMoneySelected }) => {
  const result = {
    hasError: false,
    showMoneySelectionError: false,
    showShopNameError: false,
    emailError: false,
    emptyFieldErrors: {},
    formatFieldErrors: {},
  };

  if (!isMoneySelected) {
    result.showMoneySelectionError = true;
    result.hasError = true;
  }

  if (isEmpty(shopForm.shop_name)) {
    result.showShopNameError = true;
    result.hasError = true;
  }

  // 業態(usage_type) はフロントで必須。加盟組織/発行体いずれの経路でも入力必須。
  if (isEmpty(shopForm.usage_type)) {
    result.emptyFieldErrors.usage_type = true;
    result.hasError = true;
  }

  // 加盟組織モード: 申請 API の必須項目をクライアント側でも空チェック
  if (isMemberOrgUser) {
    MEMBER_ORG_REQUIRED_FIELDS.forEach((key) => {
      if (isEmpty(shopForm[key])) {
        result.emptyFieldErrors[key] = true;
        result.hasError = true;
      }
    });
  }

  // フォーマット validation (値が入っているときのみ)
  const furiganaErr1 = validateFurigana(shopForm.shop_name_furigana);
  if (furiganaErr1) {
    result.formatFieldErrors.shop_name_furigana = furiganaErr1;
    result.hasError = true;
  }
  const furiganaErr2 = validateFurigana(shopForm.contact_person_name_furigana);
  if (furiganaErr2) {
    result.formatFieldErrors.contact_person_name_furigana = furiganaErr2;
    result.hasError = true;
  }
  const telErr = validateTel(shopForm.shop_tel);
  if (telErr) {
    result.formatFieldErrors.shop_tel = telErr;
    result.hasError = true;
  }
  const postalErr = validatePostalCode(shopForm.shop_postal_code);
  if (postalErr) {
    result.formatFieldErrors.shop_postal_code = postalErr;
    result.hasError = true;
  }

  // メールアドレス: 値あり時に形式チェック。空チェックは isMemberOrgUser のとき emptyFieldErrors で対応済。
  if (shopForm.shop_email) {
    const isEmailValid = validateEmail(shopForm.shop_email);
    if (typeof isEmailValid === 'string') {
      result.emailError = isEmailValid;
      result.hasError = true;
    }
  }

  return result;
};
